import React, { useEffect } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MUIDataTableColumn } from "mui-datatables";
import { ThemedTable } from "../../../../../common/tables/ThemedTable";
import colors from "common/styles/colors";
import { LoadingSpinner } from "../../../../../common";
import { companyDialogTableOptions, headerCellProps } from "../../utils/companyDialogTableOptions";
import CloseIcon from "@mui/icons-material/Close";
import { companyAdminsDialogStyles } from "./styles/CompanyAdminsDialog.styles";
import { useExhibitorDetailsData } from "../../hooks/useExhibitorDetailsData";
import { Nullable } from "../../../../../../common/types";

type CompanyAdminsDialogProps = {
  open: boolean;
  onClose: () => void;
  participatingOrganisationId: Nullable<string>;
  companyName?: string;
};

export const CompanyAdminsDialog: React.FC<CompanyAdminsDialogProps> = ({ open, onClose, participatingOrganisationId, companyName }) => {
  const { t } = useTranslation("common");
  const { admins, fetchAdmins, isAdminsLoading } = useExhibitorDetailsData(participatingOrganisationId);

  const columns: MUIDataTableColumn[] = [
    {
      name: "name",
      label: t("companyPage.table.name"),
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: headerCellProps
      }
    },
    {
      name: "email",
      label: t("companyPage.table.email"),
      options: {
        sort: false,
        filter: false,
        setCellHeaderProps: headerCellProps,
        customBodyRender: (value: string) => (
          <a style={{ textDecoration: "none", color: colors.primary, fontSize: 14, fontWeight: 600 }} href={`mailto:${value}`}>
            {value}
          </a>
        )
      }
    }
  ];

  useEffect(() => {
    if (!open || !participatingOrganisationId) return;
    fetchAdmins();
  }, [open, participatingOrganisationId]);

  const hasAdmins = admins.length > 0;
  const showNoAdminsState = !isAdminsLoading && !hasAdmins;

  const dialogTitle = showNoAdminsState ? t("companyPage.adminsDialog.noAdminsTitle") : t("companyPage.adminsDialog.title");

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      maxWidth={showNoAdminsState ? "xs" : "sm"}
      fullWidth
      sx={companyAdminsDialogStyles.dialogContainer}
    >
      <DialogTitle sx={companyAdminsDialogStyles.dialogTitleContainer}>
        <Typography sx={companyAdminsDialogStyles.dialogTitle}>{dialogTitle}</Typography>
        <CloseIcon color="primary" onClick={onClose} />
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5 }}>
        {isAdminsLoading && (
          <Box sx={companyAdminsDialogStyles.loading}>
            <LoadingSpinner />
          </Box>
        )}

        {showNoAdminsState && (
          <Typography variant="body1" sx={companyAdminsDialogStyles.noAdminsMessage}>
            {t("companyPage.adminsDialog.noAdminsMessage")}
          </Typography>
        )}

        {!isAdminsLoading && hasAdmins && (
          <>
            <Typography variant="body2" sx={companyAdminsDialogStyles.subtitle}>
              {t("companyPage.adminsDialog.subtitle", {
                companyName: companyName || ""
              })}
            </Typography>

            <ThemedTable cols={columns} tableData={admins} options={companyDialogTableOptions} />
          </>
        )}
      </DialogContent>

      <DialogActions sx={companyAdminsDialogStyles.dialogActions}>
        <Button variant="contained" onClick={onClose} sx={companyAdminsDialogStyles.closeButton}>
          {t("common.buttonLabels.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
